export interface PendingCompositeInstall {
  workspaceId: string;
  templateId: string;
  installRequestId: string;
  createdAt: string;
}

type MarketplaceFetch = (
  input: string,
  init?: RequestInit,
  context?: { workspaceId: string; mode: 'foreground' }
) => Promise<Response>;

export function pendingCompositeInstallKey(workspaceId: string): string {
  return `streamlain:marketplace:pending-composite-install:${workspaceId}`;
}

export function rememberPendingCompositeInstall(
  input: PendingCompositeInstall,
  storage: Pick<Storage, 'setItem'> | undefined = globalThis.localStorage
): void {
  storage?.setItem(
    pendingCompositeInstallKey(input.workspaceId),
    JSON.stringify(input)
  );
}

export function forgetPendingCompositeInstall(
  workspaceId: string,
  storage: Pick<Storage, 'removeItem'> | undefined = globalThis.localStorage
): void {
  storage?.removeItem(pendingCompositeInstallKey(workspaceId));
}

export function readPendingCompositeInstall(
  workspaceId: string,
  storage:
    | Pick<Storage, 'getItem' | 'removeItem'>
    | undefined = globalThis.localStorage
): PendingCompositeInstall | null {
  const raw = storage?.getItem(pendingCompositeInstallKey(workspaceId));
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<PendingCompositeInstall>;
    return parsed.workspaceId === workspaceId &&
      typeof parsed.templateId === 'string' &&
      typeof parsed.installRequestId === 'string' &&
      typeof parsed.createdAt === 'string'
      ? (parsed as PendingCompositeInstall)
      : null;
  } catch {
    forgetPendingCompositeInstall(workspaceId, storage);
    return null;
  }
}

export async function recoverCompositeInstallResponse(input: {
  workspaceId: string;
  installRequestId: string;
  signal: AbortSignal;
  fetcher: MarketplaceFetch;
}): Promise<Response | null> {
  const response = await input.fetcher(
    `/api/workspace/${input.workspaceId}/marketplace/installs/recovery?installRequestId=${encodeURIComponent(input.installRequestId)}`,
    { credentials: 'include', signal: input.signal }
  );
  return response.ok ? response : null;
}

export async function completeInstallWithRecovery(input: {
  url: string;
  workspaceId: string;
  installRequestId: string;
  body: Record<string, unknown>;
  signal: AbortSignal;
  fetcher: MarketplaceFetch;
}): Promise<Response> {
  let lastError: unknown;
  for (let attempt = 0; attempt < 2; attempt += 1) {
    try {
      const response = await input.fetcher(
        input.url,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(input.body),
          signal: input.signal,
        },
        { workspaceId: input.workspaceId, mode: 'foreground' }
      );
      if (response.ok || response.status < 500) return response;
      lastError = new Error(`Marketplace install failed: ${response.status}`);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw error;
      }
      lastError = error;
    }
    const recovery = await recoverCompositeInstallResponse({
      ...input,
      fetcher: input.fetcher,
    }).catch(() => null);
    if (recovery) return recovery;
  }
  throw lastError instanceof Error
    ? lastError
    : new Error('Marketplace install failed');
}
